import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Toast, ToastContainer } from "react-bootstrap";

/**
 * One fixed toast stack for the whole app.
 *
 * Every toast here reports a *state* rather than an event ("this preview is stale",
 * "these edits are not saved yet"), so none of them auto-dismiss: they are shown for
 * exactly as long as the condition holds and disappear when it clears. That is also why
 * the API is declarative -- a component says what is true right now via `useToast` and
 * never fires a one-off notification.
 *
 * A single container is what makes several toasts stack instead of landing on top of each
 * other: react-bootstrap positions the container, not the individual toasts, so mounting
 * one per component would overlap them in the same corner.
 */
/**
 * Plain strings rather than ReactNode on purpose: a spec is rebuilt on every render of
 * the calling component, so it has to be comparable by value. JSX children would be a new
 * object each time, the effect below would fire every render, and setting state from it
 * would loop.
 */
export type ToastSpec = {
  title: string;
  body: string;
  // Secondary line, rendered small and monospaced -- for a raw error message under a
  // sentence explaining it.
  detail?: string;
  // Maps to react-bootstrap's Toast `bg`. Prefer "warning" for a recoverable state the
  // user is expected to pass through while working; "danger" for something actually wrong.
  variant?: "warning" | "danger" | "info" | "success";
};

type ToastContextValue = {
  setToast: (key: string, spec: ToastSpec | null) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Record<string, ToastSpec>>({});
  // Dismissing is per-appearance, not permanent: the key is dropped from here as soon as
  // its condition clears, so the next occurrence shows up again instead of staying hidden
  // for the rest of the session.
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  const setToast = useCallback((key: string, spec: ToastSpec | null) => {
    setToasts((current) => {
      if (!spec) {
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key];
        return next;
      }
      // Bail out when nothing changed: callers pass a fresh object literal on every
      // render, so comparing by value is what keeps this from looping.
      const previous = current[key];
      if (
        previous &&
        previous.title === spec.title &&
        previous.body === spec.body &&
        previous.detail === spec.detail &&
        previous.variant === spec.variant
      ) {
        return current;
      }
      return { ...current, [key]: spec };
    });

    if (!spec) setDismissed((current) => ({ ...current, [key]: false }));
  }, []);

  const value = useMemo(() => ({ setToast }), [setToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer position="bottom-end" containerPosition="fixed" className="p-3">
        {Object.entries(toasts).map(([key, spec]) => (
          <Toast
            key={key}
            show={!dismissed[key]}
            onClose={() => setDismissed((current) => ({ ...current, [key]: true }))}
            // Deliberately no `bg`: Bootstrap's contextual toast backgrounds are fully
            // saturated and read as an alarm next to Watchtower's white cards and subtle
            // tinted alerts. The variant becomes a small accent instead -- see styles.scss.
            className={`app-toast app-toast--${spec.variant ?? "warning"} toast-slide-in`}
          >
            <Toast.Header closeButton className="app-toast__header">
              <span className="app-toast__dot" />
              <strong className="me-auto">{spec.title}</strong>
            </Toast.Header>
            <Toast.Body className="app-toast__body">
              {spec.body}
              {spec.detail && <div className="app-toast__detail">{spec.detail}</div>}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
}

/**
 * Shows `spec` for as long as it is non-null, under a key unique to the caller. Pass null
 * (or unmount) to take it down again.
 */
export function useToast(key: string, spec: ToastSpec | null) {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within a ToastHost");
  const { setToast } = context;

  // Spread into primitives so the effect compares by value, not by spec identity.
  const title = spec?.title;
  const body = spec?.body;
  const detail = spec?.detail;
  const variant = spec?.variant;

  useEffect(() => {
    setToast(
      key,
      title !== undefined && body !== undefined
        ? { title, body, detail, variant }
        : null,
    );
  }, [setToast, key, title, body, detail, variant]);

  // Leaving the screen has to take the toast with it, or it would outlive the thing it
  // describes -- navigating away from a template must not leave its preview warning up.
  useEffect(() => () => setToast(key, null), [setToast, key]);
}
